import { computed, ref, toValue, watch, type MaybeRef } from 'vue';
import { useAsyncState } from '@vueuse/core';

import type { IDadataAddress } from '../model';

const DADATA_TOKEN = '24cf832c4456ea8fdb46ffeccf9bfda840850eb1';
const DADATA_BASE_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs';

type DadataSuggestionsResponse = {
  suggestions: IDadataAddress[];
};

type AddressSuggestPayload = {
  count: number;
  division?: string;
  query: string;
};

async function dadataPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${DADATA_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Token ${DADATA_TOKEN}`
    },
    body: JSON.stringify(body)
  });

  return res.json() as Promise<T>;
}

export const fetchDadataAddressByQuery = async (
  query: string,
  division = 'administrative'
): Promise<IDadataAddress | null> => {
  const trimmed = query.trim();
  if (trimmed.length < 3) return null;

  const data = await dadataPost<DadataSuggestionsResponse>('/suggest/address', {
    query: trimmed,
    count: 1,
    division
  });

  return data.suggestions?.[0] ?? null;
};

export const useDadataAddress = ({
  searchQuery,
  division,
  queryEnabled
}: {
  searchQuery: MaybeRef<string>;
  queryEnabled: MaybeRef<boolean>;
  division: MaybeRef<string>;
}) => {
  const payload = computed(() => ({
    count: 10,
    division: toValue(division),
    query: toValue(searchQuery)
  }));

  const { execute, isLoading, state } = useAsyncState(
    (body: AddressSuggestPayload) => dadataPost<DadataSuggestionsResponse>('/suggest/address', body),
    null as DadataSuggestionsResponse | null,
    { immediate: false }
  );

  watch(payload, to => {
    if (toValue(queryEnabled)) execute(0, to);
  });

  const suggestions = ref<IDadataAddress[]>([]);
  watch(state, to => {
    suggestions.value = to?.suggestions ?? [];
  });

  return { suggestions, isLoading };
};

export const useDadataBank = ({
  searchQuery,
  queryEnabled
}: {
  searchQuery: MaybeRef<string>;
  queryEnabled: MaybeRef<boolean>;
}) => {
  const payload = computed(() => ({ count: 10, query: toValue(searchQuery) }));

  const { execute, isLoading, state } = useAsyncState(
    (body: { count: number; query: string }) =>
      dadataPost<{ suggestions: unknown[] }>('/suggest/bank', body),
    null as { suggestions: unknown[] } | null,
    { immediate: false }
  );

  watch(payload, to => {
    if (toValue(queryEnabled)) execute(0, to);
  });

  const suggestions = ref<unknown[]>([]);
  watch(state, to => {
    suggestions.value = to?.suggestions ?? [];
  });

  return { suggestions, isLoading };
};

export const useDadataFMS = ({
  searchQuery,
  queryEnabled
}: {
  searchQuery: MaybeRef<string>;
  queryEnabled: MaybeRef<boolean>;
}) => {
  const payload = computed(() => ({ count: 10, query: toValue(searchQuery) }));

  const { execute, isLoading, state } = useAsyncState(
    (body: { count: number; query: string }) =>
      dadataPost<{ suggestions: unknown[] }>('/suggest/fms_unit', body),
    null as { suggestions: unknown[] } | null,
    { immediate: false }
  );

  watch(payload, to => {
    if (toValue(queryEnabled)) execute(0, to);
  });

  const suggestions = ref<unknown[]>([]);
  watch(state, to => {
    suggestions.value = to?.suggestions ?? [];
  });

  return { suggestions, isLoading };
};

export const useDadataParty = ({
  searchQuery,
  queryEnabled
}: {
  searchQuery: MaybeRef<string>;
  queryEnabled: MaybeRef<boolean>;
}) => {
  const payload = computed(() => ({ count: 10, query: toValue(searchQuery) }));

  const { execute, isLoading, state } = useAsyncState(
    (body: { count: number; query: string }) =>
      dadataPost<{ suggestions: unknown[] }>('/suggest/party', body),
    null as { suggestions: unknown[] } | null,
    { immediate: false }
  );

  watch(payload, to => {
    if (toValue(queryEnabled)) execute(0, to);
  });

  const suggestions = ref<unknown[]>([]);
  watch(state, to => {
    suggestions.value = to?.suggestions ?? [];
  });

  return { suggestions, isLoading };
};
